import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import { useNavigate, useParams } from 'react-router-dom';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import CheckIcon from '@mui/icons-material/Check';

import GratiPageWrapper, { WrapperAction } from '../components/common/GratiPageWrapper';
import DivisionEditor from '../components/division/DivisionEditor';
import { useApp } from '../contextProviders/AppProvider';
import { useData } from '../contextProviders/DataProvider';
import useLeague from '../dataHooks/useLeague';
import { useLeagueDivisionMutator } from '../dataMutators/useLeagueDivisionMutator';
import DefaultObjects from '../types/DefaultObjects';
import Grouping from '../types/Grouping';
import Person from '../types/Person';
import { generateFirestoreId } from '../utils/utils';

export default function DivisionCreatePage(): ReactElement {
  const { orgId, leagueId } = useParams();
  const navigate = useNavigate();
  const { setPageName } = useApp();
  const { isLeagueAdmin, userState } = useData();
  const { addDivision } = useLeagueDivisionMutator();

  const { league, isLeagueLoading, leagueError } = useLeague({ orgId, leagueId });

  const initialDivision = useMemo((): Grouping => ({
    ...DefaultObjects.Division,
    itemId: generateFirestoreId(),
    creatorId: userState.userProfile?.itemId ?? '',
    orgId: league?.orgId ?? orgId ?? '',
    orgName: league?.orgName ?? '',
    leagueId: league?.itemId ?? leagueId ?? '',
    createTime: Timestamp.now(),
  }), [league, orgId, leagueId, userState.userProfile?.itemId]);

  const [pageActions, setPageActions] = useState<WrapperAction | null>(null);
  const [divisionName, setDivisionName] = useState('');
  const currentDivisionRef = useRef<Grouping>(initialDivision);

  useEffect(() => { setPageName('Add division'); }, [setPageName]);

  const handleCancel = useCallback(() => navigate(-1), [navigate]);

  const handleCreate = useCallback(async () => {
    if (!league) return;
    const divisionToSave = Object.fromEntries(
      Object.entries(currentDivisionRef.current).filter(([, v]) => v !== undefined)
    ) as Grouping;
    const created = await addDivision(league, divisionToSave);
    if (created) navigate(-1);
  }, [league, addDivision, navigate]);

  const handleDivisionChange = useCallback((division: Grouping) => {
    currentDivisionRef.current = division;
    setDivisionName(division.name);
  }, []);

  const handleCancelRef = useRef(handleCancel);
  const handleCreateRef = useRef(handleCreate);
  useEffect(() => {
    handleCancelRef.current = handleCancel;
    handleCreateRef.current = handleCreate;
  });

  useEffect(() => {
    setPageActions({
      title: { sm: 'Add division' },
      actions: (
        <Stack direction="row" spacing={2} sx={{
          alignItems: "center"
        }}>
          <Button size="small" variant="text" color="secondary" onClick={() => handleCancelRef.current()}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<CheckIcon />}
            onClick={() => handleCreateRef.current()}
            disabled={!divisionName.trim()}
          >
            Create
          </Button>
        </Stack>
      ),
    });
  }, [divisionName]);

  const validateUser = (userProfile: Person | null | undefined) =>
    !!(userProfile && leagueId && isLeagueAdmin(leagueId, league?.orgId ?? ''));

  return (
    <GratiPageWrapper
      validateUser={validateUser}
      isContentLoading={isLeagueLoading && !league}
      contentError={leagueError}
      pageActions={pageActions}
    >
      <DivisionEditor
        division={initialDivision}
        isNew
        onDivisionChange={handleDivisionChange}
      />
    </GratiPageWrapper>
  );
}
